import React, { useState, useCallback, useEffect, memo } from 'react';
import {
  isImageFile,
  isPdfFile,
  triggerBlobDownload,
  getAttachmentName
} from '../../utils/attachmentUtils';
import type { AttachmentItem, SavedAttachment } from '../../types/attachments';
import { DOCUMENT_HINT } from '../../constants/attachments';

export interface AttachmentCarouselSlideProps {
  item: AttachmentItem | SavedAttachment;
}

function hasFile(
  item: AttachmentItem | SavedAttachment
): item is (AttachmentItem | SavedAttachment) & { file: File } {
  return 'file' in item && !!item.file;
}

function hasUrlOnly(item: AttachmentItem | SavedAttachment): boolean {
  return 'url' in item && !!item.url && !hasFile(item);
}

function AttachmentCarouselSlideComponent({
  item
}: AttachmentCarouselSlideProps): React.ReactElement {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const useBlobPath = hasFile(item);
  const useUrlPath = hasUrlOnly(item);
  const file = useBlobPath ? item.file : undefined;
  const downloadUrl = useUrlPath ? (item as SavedAttachment).url : undefined;
  const needBlobUrl =
    !!file &&
    (isPdfFile(item) ||
      (isImageFile(item) && !(item as AttachmentItem).previewUrl));

  useEffect(() => {
    if (!needBlobUrl || !file) {
      setBlobUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setBlobUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      setBlobUrl(null);
    };
  }, [needBlobUrl, file]);

  const displayUrl = useBlobPath
    ? isImageFile(item) && (item as AttachmentItem).previewUrl
      ? (item as AttachmentItem).previewUrl
      : blobUrl ?? undefined
    : downloadUrl;

  const displayName = getAttachmentName(item);
  const handleDownload = useCallback(() => {
    if (useBlobPath && file) {
      triggerBlobDownload(file, displayName);
      return;
    }
    if (downloadUrl) {
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = displayName;
      a.rel = 'noopener noreferrer';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }, [useBlobPath, file, downloadUrl, displayName]);

  const isImage = isImageFile(item);
  const isPdf = isPdfFile(item);
  const canPreview = isImage || isPdf;
  const showDownload = (useBlobPath && file) || downloadUrl;

  if (canPreview && displayUrl) {
    return isImage ? (
      <img
        src={displayUrl}
        alt={displayName}
        className="attachment-carousel__media attachment-carousel__image"
      />
    ) : (
      <embed
        src={displayUrl}
        type="application/pdf"
        className="attachment-carousel__media attachment-carousel__pdf"
        title={displayName}
      />
    );
  }

  return (
    <div className="attachment-carousel__media attachment-carousel__document">
      <span className="attachment-carousel__doc-icon" aria-hidden>
        📄
      </span>
      <p className="attachment-carousel__doc-name">{displayName}</p>
      <p className="attachment-carousel__doc-hint">{DOCUMENT_HINT}</p>
      {showDownload && (
        <button
          type="button"
          onClick={handleDownload}
          className="attachment-carousel__download"
        >
          Download
        </button>
      )}
    </div>
  );
}

export const AttachmentCarouselSlide = memo(AttachmentCarouselSlideComponent);
AttachmentCarouselSlide.displayName = 'AttachmentCarouselSlide';
